import React from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  Divider,
  Paper,
} from "@mui/material";
import { 
  Facebook, 
  Instagram, 
  Twitter, 
  Phone, 
  Email, 
  LocationOn,
  Schedule 
} from "@mui/icons-material";

const Footer = ({ restaurantInfo }) => {
  return (
    <Box
      sx={{
        position: "relative",
        background: `
          linear-gradient(
            rgba(10, 10, 10, 0.85),
            rgba(20, 20, 20, 0.95)
          ),
          url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 800"><defs><pattern id="food" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="50" cy="50" r="2" fill="%234caf50" opacity="0.3"/><path d="M30,30 Q50,10 70,30 T90,30" fill="none" stroke="%234caf50" stroke-width="1" opacity="0.2"/><rect x="20" y="70" width="10" height="20" fill="%2381c784" opacity="0.2"/><circle cx="80" cy="80" r="8" fill="%23ff9800" opacity="0.2"/></pattern></defs><rect width="100%" height="100%" fill="url(%23food)"/></svg>')
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        color: "white",
        py: 6,
        mt: "auto",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(
              circle at 20% 80%,
              rgba(76, 175, 80, 0.1) 0%,
              transparent 50%
            ),
            radial-gradient(
              circle at 80% 20%,
              rgba(255, 152, 0, 0.1) 0%,
              transparent 50%
            )
          `,
          zIndex: 0,
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={4}>
          {/* Restaurant Info */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                background: "rgba(30, 30, 30, 0.6)",
                backdropFilter: "blur(20px)",
                borderRadius: 3,
                p: 3,
                border: "1px solid rgba(255, 255, 255, 0.1)",
                height: "100%",
              }}
            >
              <Typography 
                variant="h4"
                sx={{
                  color: '#4caf50',
                  fontFamily: '"Playfair Display", serif',
                  fontWeight: 700,
                  letterSpacing: '1px',
                  mb: 2,
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    width: '60%',
                    height: '3px',
                    background: 'linear-gradient(90deg, #4caf50, transparent)',
                    borderRadius: '3px'
                  }
                }}
              >
                {restaurantInfo.name}
              </Typography>
              
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <LocationOn sx={{ color: "#4caf50", mr: 1, fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: "#e0e0e0" }}>
                  {restaurantInfo.address}
                </Typography>
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Phone sx={{ color: "#4caf50", mr: 1, fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: "#e0e0e0" }}>
                  {restaurantInfo.phone}
                </Typography>
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Email sx={{ color: "#4caf50", mr: 1, fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: "#e0e0e0" }}>
                  {restaurantInfo.email}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Opening Hours */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                background: "rgba(30, 30, 30, 0.6)",
                backdropFilter: "blur(20px)",
                borderRadius: 3,
                p: 3,
                border: "1px solid rgba(255, 255, 255, 0.1)",
                height: "100%",
              }}
            >
              <Typography 
                variant="h5" 
                sx={{ 
                  color: "white", 
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  fontWeight: 600,
                }}
              >
                <Schedule sx={{ mr: 1, color: "#4caf50" }} />
                Opening Hours
              </Typography>
              
              <Box sx={{ color: "#e0e0e0" }}>
                {restaurantInfo.hours?.split('\n').map((line, index) => (
                  <Typography 
                    key={index} 
                    variant="body2" 
                    sx={{ 
                      mb: 1.5,
                      display: "flex",
                      justifyContent: "space-between",
                      "&:not(:last-child)": {
                        pb: 1,
                        borderBottom: "1px solid rgba(255,255,255,0.1)"
                      }
                    }}
                  >
                    {line}
                  </Typography>
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* Social Media & Design Credit */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                background: "rgba(30, 30, 30, 0.6)",
                backdropFilter: "blur(20px)",
                borderRadius: 3,
                p: 3,
                border: "1px solid rgba(255, 255, 255, 0.1)",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    color: "white", 
                    mb: 3,
                    fontWeight: 600,
                  }}
                >
                  Follow Us
                </Typography>
                
                <Box display="flex" gap={2} mb={4}>
                  <IconButton 
                    sx={{
                      background: "linear-gradient(135deg, #1877f2, #0d5cb6)",
                      color: "white",
                      "&:hover": {
                        background: "linear-gradient(135deg, #0d5cb6, #1877f2)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 20px rgba(24, 119, 242, 0.4)",
                      },
                      transition: "all 0.3s ease",
                      width: 48,
                      height: 48,
                    }}
                  >
                    <Facebook />
                  </IconButton>
                  <IconButton 
                    sx={{
                      background: "linear-gradient(135deg, #e4405f, #c13584)",
                      color: "white",
                      "&:hover": {
                        background: "linear-gradient(135deg, #c13584, #e4405f)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 20px rgba(228, 64, 95, 0.4)",
                      },
                      transition: "all 0.3s ease",
                      width: 48,
                      height: 48,
                    }}
                  >
                    <Instagram />
                  </IconButton>
                  <IconButton 
                    sx={{
                      background: "linear-gradient(135deg, #1da1f2, #0d8bd9)",
                      color: "white",
                      "&:hover": {
                        background: "linear-gradient(135deg, #0d8bd9, #1da1f2)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 8px 20px rgba(29, 161, 242, 0.4)",
                      },
                      transition: "all 0.3s ease",
                      width: 48,
                      height: 48,
                    }}
                  >
                    <Twitter />
                  </IconButton>
                </Box>
              </Box>

              {/* Design Credit */}
              <Box
                sx={{
                  textAlign: "center",
                  pt: 2,
                  borderTop: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: "#a0aec0",
                    display: "block",
                    mb: 1,
                  }}
                >
                  Designed by
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{
                    background: "linear-gradient(135deg, #4caf50, #81c784, #4caf50)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                    letterSpacing: "1px",
                  }}
                >
                  ELEGANTÉ IT
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Bottom Divider and Copyright */}
        <Divider sx={{ my: 4, backgroundColor: "rgba(255, 255, 255, 0.1)" }} />
        
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ color: "#a0aec0" }}
          >
            © {new Date().getFullYear()} {restaurantInfo.name}. All rights reserved.
          </Typography>
          
          <Box sx={{ display: "flex", gap: 3 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: "#a0aec0",
                cursor: "pointer",
                "&:hover": { color: "#4caf50" },
                transition: "color 0.3s ease"
              }}
            >
              Privacy Policy
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: "#a0aec0",
                cursor: "pointer",
                "&:hover": { color: "#4caf50" },
                transition: "color 0.3s ease"
              }}
            >
              Terms of Service
            </Typography>
          </Box>
        </Box>
      </Container>

      {/* Decorative Elements */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "4px",
          background: "linear-gradient(90deg, #4caf50, #ff9800, #4caf50)",
          opacity: 0.6,
        }}
      />
    </Box>
  );
};

// Default restaurant info in case props are not provided
Footer.defaultProps = {
  restaurantInfo: {
    name: "Green Velvet",
    address: "123 Culinary Avenue, Food District",
    phone: "+234 706 868 6839",
    email: "info@greenvelvet.com",
    hours: "Monday - Friday: 8:00 AM - 10:00 PM\nSaturday: 9:00 AM - 11:00 PM\nSunday: 10:00 AM - 9:00 PM"
  }
};

export default Footer;